import { useState } from 'react';
import { Button } from '@/components/ui/Button';

export interface RejectClosureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (note: string) => Promise<void>;
}

export function RejectClosureModal({ isOpen, onClose, onSubmit }: RejectClosureModalProps) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) { return null; }

  const trimmed = note.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= 2000;

  const handleSubmit = async () => {
    if (!canSubmit) { return; }
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setNote('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16">
      <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
          Reject Closure
        </h2>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
          The requester will be notified with your reason. The incident will return to In Progress.
        </p>
        <label
          htmlFor="closure-rejection-note"
          className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1"
        >
          Reason for rejection <span className="text-danger-600">*</span>
        </label>
        <textarea
          id="closure-rejection-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={2000}
          required
          placeholder="Explain what's needed before this can close..."
          className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <div className="mt-4 flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={!canSubmit}>
            Reject Closure
          </Button>
        </div>
      </div>
    </div>
  );
}
